import type { UserModel } from "@eu.mdotm.api/sphere";
import { startImpersonating } from "$root/functional-areas/user/auth";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { ToastableError } from "$root/utils";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { Icon, Text, colorBySeverity } from "@mdotm/shadcn/legacy/components";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { z } from "zod";

type ImpersonateBoxProps =
	| {
			mode: "Box";
			email?: string;
			users?: UserModel[];
	  }
	| {
			mode: "Button";
			email: string;
	  };

const ImpersonateBox: React.FC<ImpersonateBoxProps> = (props) => {
	const { mode, email: currentEmail = "" } = props;
	const history = useHistory();

	const { watch, control, formState } = useForm({
		defaultValues: { email: currentEmail },
		resolver: zodResolver(z.object({ email: z.string() })),
	});

	const [errors, setErrors] = useState<string[]>([]);
	const isButtonImpersonate = mode === "Button";

	// Impersonate User
	async function impersonate(email: string) {
		await runWithErrorReporting(
			async () => {
				try {
					setErrors([]);
					const emailValidation = z.string().email().safeParse(email);
					if (!emailValidation.success) {
						throw new ToastableError("Impersonate failed", { icon: "Top-menu-user", cause: "INVALID_FIELDS" });
					}

					await startImpersonating(email);

					history.push("/");
				} catch (error) {
					setErrors(["INVALID_FIELDS"]);
					throw error;
				}
			},
			{ area: "user", attemptedOperation: "impersonate user" },
		);
	}

	return (
		<>
			{isButtonImpersonate ? (
				<Button unstyled onClickAsync={() => impersonate(watch("email"))}>
					{(state) => (
						<Icon
							style={{
								animationDirection: "reverse",
							}}
							className={{ "animate-spin": state.kind === "loading" }}
							color={colorBySeverity.success}
							size={16}
							icon="Sync"
						/>
					)}
				</Button>
			) : (
				<div
					data-qualifier="ImpersonateBox"
					style={{
						borderLeft: `8px solid #4cb09c`,
						background: "white",
						padding: 16,
						marginBottom: 16,
						borderRadius: `0 6px 6px 0`,
					}}
				>
					<Text type="Body/XL/Bold">Impersonate [Admin only]</Text>
					<Form onSubmitAsync={() => impersonate(watch("email"))} noValidate style={{ marginTop: "0.85rem" }}>
						<div style={{ width: 384 }}>
							<FormFields.Autocomplete
								formState={formState}
								control={control}
								label="Email"
								name="email"
								className="w-full"
								invalid={errors.length > 0}
								options={props.users?.map((user) => ({ label: user.email ?? "", value: user.email ?? "" })) ?? []}
							/>
						</div>
						<div className="flex justify-end mt-4">
							<SubmitButton data-qualifier="ImpersonateBox/Submit">Impersonate</SubmitButton>
						</div>
					</Form>
				</div>
			)}
		</>
	);
};

export default ImpersonateBox;
